import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { EnvService } from '@core-mkt/services/env/env.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';

@Component({
  selector: 'tier-badge',
  templateUrl: './tier-badge.component.html',
  standalone: true,
  imports: [CommonModule],
})
export class TierBadgeComponent {
  @Input() topContent: string;
  @Input() topContentStyles: string[];
  @Input() topBackgroundColor: string;
  @Input() topTextColor: string;

  constructor(private themeParser: ThemeParserService, private env: EnvService) {}

  /**
   * NOTE: This component intentionally reads `designMode` via a getter rather than using
   * `DesignModeDirective` (`*appDesignMode`) in the template. `DesignModeDirective` creates an
   * embedded view once in `ngOnInit` and Angular 20's `@if`/`@else` blocks inside a custom
   * structural directive's embedded view can get stuck at their initial evaluation — meaning the
   * `useGradientPhil(topContentStyles)` condition would not re-evaluate correctly when inputs
   * change after the view is first created. Using a flat `@if(isAceDesign && ...)` structure at
   * the template root avoids the nesting issue entirely and is consistent with how `BundleComponent`
   * and `CardItemComponent` resolve design mode.
   */
  get isAceDesign(): boolean {
    return (this.env.get.brandConfig.designMode ?? 'ace') === 'ace';
  }

  /**
   * Converts an array of CSS class names to a space-separated string
   * @param topContentStyles Array of CSS class names
   * @returns Space-separated string of CSS classes
   */
  getTopContentStyles(topContentStyles: string[]): string {
    return topContentStyles?.toString().replaceAll(',', ' ') || '';
  }

  /**
   * Checks if the gradient badge variant should be used
   * @param topContentStyles Array of CSS class names to check
   * @returns True if the gradient Phil variant should be used
   */
  useGradientPhil(topContentStyles: string[]): boolean {
    const gradientPhil = 'tier-gradient';
    return topContentStyles?.includes(gradientPhil) || false;
  }

  /**
   * Generates theme configuration for DEC/AARP badge styling
   * @param backgroundColor Background color for the badge
   * @param textColor Text color for the badge
   * @returns Theme configuration object
   */
  getThemeForTopBanner(backgroundColor: string, textColor: string): ThemeInterface {
    if (backgroundColor === '') {
      const theme: ThemeInterface = {
        root: '',
        container: '',
        columnOrder: '',
        bgImage: '',
      };
      return theme;
    } else {
      const themeInput: ComponentTheme = {
        styles: [],
        backgroundColor: backgroundColor,
        backgroundImage: null,
        textColor: textColor,
        columnCount: 0,
        columnDistribution: '',
      };
      return this.themeParser.getThemeClasses(themeInput);
    }
  }
}
